"use client";

import { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function IgniteYourLeadership() {
  const videoRef = useRef<HTMLDivElement | null>(null);
  const [scrollY, setScrollY] = useState(0);
  const {
    ref: captionRef,
    isVisible: captionVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>();
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLParagraphElement>();
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="2985" id="ignite-your-leadership" className="relative min-h-[80vh] md:min-h-screen w-full overflow-hidden bg-background text-foreground">
      {}
      <div ref={videoRef} className="absolute inset-0 w-full h-[120%] will-change-transform" style={{
      transform: `translateY(${scrollY * 0.3}px)`
    }}>
        <video className="absolute inset-0 w-full h-full object-cover grayscale contrast-125 scale-x-[-1]" autoPlay loop muted playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_stage.png">
          <source src="/img/leadership_performance_highlights.webm" type="video/webm" />
        </video>
        {}
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_stage.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_stage.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698923/img/damien_cole_keynote_stage.png-wvc-sizes" alt="Damien Cole speaking on a high-energy stage" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover -z-10 scale-x-[-1]" />
      </div>

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "linear-gradient(180deg, hsla(215, 25%, 10%, 0.4) 0%, hsla(215, 25%, 10%, 0.7) 60%, hsla(215, 25%, 10%, 0.95) 100%)"
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.06] mix-blend-overlay" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {}
      <div className="absolute inset-0 pointer-events-none" style={{
      background: "radial-gradient(ellipse at center, transparent 40%, hsla(215, 25%, 10%, 0.6) 100%)"
    }} />

      {}
      <div className="relative z-10 min-h-[80vh] md:min-h-screen w-full max-w-[1440px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-12 min-h-[80vh] md:min-h-screen">
          <div className="col-span-12 lg:col-span-7 xl:col-span-6 flex flex-col justify-end pb-20 sm:pb-28 lg:pb-32 pt-32">
            {}
            <div ref={captionRef} className={`font-mono text-[11px] sm:text-xs uppercase tracking-[0.2em] mb-8 transition-all duration-1000 ease-out ${captionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            color: "hsl(45 100% 50%)"
          }}>
              <span className="inline-flex items-center gap-3">
                <span className="inline-block w-8 h-px" style={{
                backgroundColor: "hsl(45 100% 50%)"
              }} />
                Keynote Speaker &middot; Leadership Coach
              </span>
            </div>

            {}
            <h1 ref={headlineRef} className={`font-default font-black text-foreground tracking-tighter transition-all duration-1000 delay-150 ease-out uppercase ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(48px, 9vw, 120px)",
            lineHeight: 0.9
          }}>
              <span className="block">Ignite your</span>
              <span className="block italic" style={{
              color: "hsl(45 100% 50%)"
            }}>
                Leadership.
              </span>
            </h1>

            {}
            <p ref={bodyRef} className={`font-default mt-8 max-w-xl text-lg sm:text-xl leading-relaxed transition-all duration-1000 delay-300 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
            color: "hsl(215 15% 60%)"
          }}>
              High-octane strategies for the resilience mindset and leading with empathy in a volatile world.
            </p>

            {}
            <div ref={ctaRef} className={`mt-12 flex flex-col sm:flex-row gap-4 transition-all duration-1000 delay-500 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild size="lg" className="font-default font-extrabold text-sm uppercase tracking-wider h-12 px-8 bg-primary text-primary-foreground hover:bg-ash-white transition-colors rounded-md">
                <Link to="/DamienColePage">Book Damien Now</Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="font-default font-extrabold text-sm uppercase tracking-wider h-12 px-8 bg-transparent rounded-md border-2" style={{
              borderColor: "hsl(45 100% 50%)",
              color: "hsl(45 100% 50%)"
            }}>
                <Link to="#keynote-topics">View Keynotes</Link>
              </Button>
            </div>
          </div>
        </div>

        {}
        <div className="hidden md:flex absolute right-8 lg:right-16 bottom-12 flex-col items-center gap-4 z-10">
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]" style={{
          color: "hsl(45 100% 50%)",
          writingMode: "vertical-rl",
          transform: "rotate(180deg)"
        }}>
            Momentum
          </span>
          <div className="relative h-[60px] w-px overflow-hidden" style={{
          backgroundColor: "hsla(215, 15%, 60%, 0.2)"
        }}>
            <span className="absolute top-0 left-0 w-px h-[30px] animate-[scrollLine_2s_ease-in-out_infinite]" style={{
            backgroundColor: "hsl(45 100% 50%)"
          }} />
          </div>
        </div>

        {}
        <div className="hidden md:flex absolute left-6 sm:left-10 lg:left-16 bottom-12 items-center gap-3 z-10">
          <span className="inline-block w-6 h-px" style={{
          backgroundColor: "hsla(45, 100%, 50%, 0.4)"
        }} />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{
          color: "hsla(210, 20%, 98%, 0.55)"
        }}>
            The Command Stage &middot; Global Impact
          </span>
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(200%); }
        }
      `}</style>
    </section>;
}